import { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { ResponseArray } from '@/types/shared';
import type { ContractListParams } from '@/api/contracts';
import { ContractListToolbar, colId, type SectionColumn } from './contract-list-toolbar';
import { paginationView } from '@/pages/customers/pagination-summary';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Shape of the react-query result the section drives its table from. */
export interface ListQueryResult<Row> {
  data?: ResponseArray<Row>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

export interface ContractListSectionProps<Row> {
  /** Runs the list query for the given params (page/search/filters). */
  useListQuery: (params: ContractListParams) => ListQueryResult<Row>;
  columns: SectionColumn<Row>[];
  getRowId: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  filterFields?: FilterField[];
  searchPlaceholder?: string;
  errorTitle?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * Called whenever the effective query params (search + filters, no page)
   * change. Used by the parent to thread the same params into the stat band.
   */
  onParamsChange?: (params: ContractListParams) => void;
}

/**
 * Server-paginated contract list: Toolbar (search + Filters popover + Display
 * options) over a DataTable with optional row-click navigation and Pagination.
 * Search (`q`), applied filter conditions and page are pushed to the backend via
 * the injected `useListQuery` hook; loading → Skeleton, error → Alert+retry,
 * empty → EmptyState. Mirrors the customer `CustomerListSection` pattern,
 * generalized over the row type + query hook.
 */
export function ContractListSection<Row>({
  useListQuery,
  columns,
  getRowId,
  onRowClick,
  filterFields,
  searchPlaceholder = 'Search…',
  errorTitle = 'Failed to load contracts',
  emptyTitle = 'No contracts found',
  emptyDescription = 'Try adjusting your search or filters.',
  onParamsChange,
}: ContractListSectionProps<Row>) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [colId(c), true])),
  );

  const filters = useMemo(() => conditionsToFilters(conditions), [conditions]);

  // Notify parent whenever search or filters change (not page — stat band is filter-scoped).
  useEffect(() => {
    onParamsChange?.({
      q: query || undefined,
      filters: Object.keys(filters).length ? filters : undefined,
    });
  }, [query, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, isError, isFetching, refetch } = useListQuery({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
    filters: Object.keys(filters).length ? filters : undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[colId(c)] !== false),
    [columns, visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ContractListToolbar
        columns={columns}
        filterFields={filterFields}
        searchPlaceholder={searchPlaceholder}
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title={errorTitle}
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={getRowId}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={<FileText size={24} />}
              title={emptyTitle}
              description={emptyDescription}
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}
    </div>
  );
}

/**
 * Fold FilterBuilder conditions into the backend `filters` object. Multiselect
 * values arrive comma-joined; select/text collapse to a single-item array so the
 * backend's `IN`-style decode still applies. Multiple conditions on one field
 * merge their values.
 */
function conditionsToFilters(conditions: FilterCondition[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    const values = c.value.split(',').map((s) => s.trim()).filter(Boolean);
    out[c.field] = [...(out[c.field] ?? []), ...values];
  }
  return out;
}
